import React, { useEffect, useMemo, useState } from 'react';
import { X, FileText, Plus, Trash2, Download } from 'lucide-react';
import api from '../lib/api';

// Standalone accounting document: קבלה (RE) or דרישת תשלום (DI). Not tied to a
// donation — the user enters a client + line items and issues via Tranzila.

interface TerminalRow {
  _id: string;
  name: string;
  provider: 'hyp' | 'tranzila' | 'other';
  isDefault?: boolean;
  isActive?: boolean;
  type?: 'system' | 'organization';
}

interface LineItem {
  name: string;
  unitPrice: string;
  unitsNumber: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'cash', label: 'מזומן' },
  { value: 'check', label: "צ'ק" },
  { value: 'bank_transfer', label: 'העברה בנקאית' },
  { value: 'credit_card', label: 'כרטיס אשראי' },
  { value: 'bit', label: 'ביט' },
  { value: 'other', label: 'אחר' },
];

const CURRENCIES = ['ILS', 'USD', 'EUR', 'GBP'];

export default function CreateDocumentDialog({ open, onClose, onCreated }: Props) {
  const [documentType, setDocumentType] = useState<'RE' | 'DI'>('RE');
  const [terminals, setTerminals] = useState<TerminalRow[]>([]);
  const [terminalId, setTerminalId] = useState('');
  const [currency, setCurrency] = useState('ILS');
  const [paymentMethod, setPaymentMethod] = useState('cash');

  const [client, setClient] = useState({
    name: '', company: '', id: '', email: '', phone: '',
    addressLine1: '', city: '', zip: '',
  });
  const [items, setItems] = useState<LineItem[]>([{ name: '', unitPrice: '', unitsNumber: '1' }]);
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    // reset
    setDocumentType('RE');
    setCurrency('ILS');
    setPaymentMethod('cash');
    setClient({ name: '', company: '', id: '', email: '', phone: '', addressLine1: '', city: '', zip: '' });
    setItems([{ name: '', unitPrice: '', unitsNumber: '1' }]);
    setNotes('');
    setMessage(null);

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/terminals');
        if (cancelled) return;
        // Only Tranzila terminals support create_document (RE/DI).
        const list: TerminalRow[] = (res.data || []).filter(
          (t: any) => t.isActive !== false && t.type === 'organization' && t.provider === 'tranzila'
        );
        setTerminals(list);
        const def = list.find((t) => t.isDefault) || list[0];
        if (def) setTerminalId(def._id);
      } catch (err) {
        console.error('[CreateDocumentDialog] terminals load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const total = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.unitPrice) || 0) * (Number(it.unitsNumber) || 0), 0),
    [items]
  );

  const updateItem = (idx: number, patch: Partial<LineItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, { name: '', unitPrice: '', unitsNumber: '1' }]);
  const removeItem = (idx: number) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const downloadPdf = async (id: string) => {
    try {
      const resp = await api.get(`/accounting-documents/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setMessage({ type: 'error', text: 'המסמך הופק אך לא ניתן להוריד אותו כרגע' });
    }
  };

  const handleSubmit = async () => {
    if (!terminalId) {
      setMessage({ type: 'error', text: 'יש לבחור מסוף' });
      return;
    }
    const cleanItems = items
      .map((it) => ({ name: it.name.trim(), unitPrice: Number(it.unitPrice), unitsNumber: Number(it.unitsNumber) || 1 }))
      .filter((it) => it.name && Number.isFinite(it.unitPrice) && it.unitPrice >= 0);
    if (!cleanItems.length) {
      setMessage({ type: 'error', text: 'יש להוסיף לפחות שורת פריט אחת עם שם וסכום' });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const res = await api.post('/accounting-documents', {
        documentType,
        terminalId,
        currency,
        paymentMethod: documentType === 'RE' ? paymentMethod : undefined,
        client,
        items: cleanItems,
        notes: notes.trim() || undefined,
      });
      const docLabel = documentType === 'RE' ? 'קבלה' : 'דרישת תשלום';
      setMessage({
        type: 'success',
        text: `${docLabel} ${res.data?.documentNumber ? '#' + res.data.documentNumber + ' ' : ''}הופקה בהצלחה`,
      });
      onCreated?.();
      if (res.data?.id) void downloadPdf(res.data.id);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.error || 'שגיאה ביצירת המסמך' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const inputCls =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-[#2C2C2C] rounded-lg text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" dir="rtl" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <FileText className="text-blue-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">יצירת מסמך חשבונאי</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300" aria-label="סגור">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 overflow-auto space-y-4">
          {/* Document type */}
          <div className="grid grid-cols-2 gap-2">
            {([
              { v: 'RE', label: 'קבלה' },
              { v: 'DI', label: 'דרישת תשלום' },
            ] as const).map((opt) => (
              <button
                key={opt.v}
                onClick={() => setDocumentType(opt.v)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                  documentType === opt.v
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 dark:bg-[#2C2C2C] text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Terminal + currency + payment method */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">מסוף (Tranzila)</label>
              {terminals.length === 0 ? (
                <div className="text-xs text-red-600 dark:text-red-400 py-2">לא נמצאו מסופי Tranzila פעילים.</div>
              ) : (
                <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} className={inputCls}>
                  {terminals.map((t) => (
                    <option key={t._id} value={t._id}>{t.name}{t.isDefault ? ' (ברירת מחדל)' : ''}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">מטבע</label>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {documentType === 'RE' && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">אמצעי תשלום</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputCls}>
                  {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Client */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="text-xs uppercase text-gray-500 mb-2">פרטי הלקוח</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <input className={inputCls} placeholder="שם" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
              <input className={inputCls} placeholder="שם חברה (אופציונלי)" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} />
              <input className={inputCls} placeholder="ח.פ. / ת.ז." value={client.id} onChange={(e) => setClient({ ...client, id: e.target.value })} />
              <input className={inputCls} placeholder="אימייל" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
              <input className={inputCls} placeholder="כתובת" value={client.addressLine1} onChange={(e) => setClient({ ...client, addressLine1: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="עיר" value={client.city} onChange={(e) => setClient({ ...client, city: e.target.value })} />
                <input className={inputCls} placeholder="מיקוד" value={client.zip} onChange={(e) => setClient({ ...client, zip: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase text-gray-500">פריטים</div>
              <button onClick={addItem} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-semibold">
                <Plus size={14} /> הוסף שורה
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                  <input className={`${inputCls} col-span-6`} placeholder="תיאור" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} />
                  <input className={`${inputCls} col-span-2`} type="number" min="0" placeholder="כמות" value={it.unitsNumber} onChange={(e) => updateItem(idx, { unitsNumber: e.target.value })} />
                  <input className={`${inputCls} col-span-3`} type="number" min="0" placeholder="מחיר" value={it.unitPrice} onChange={(e) => updateItem(idx, { unitPrice: e.target.value })} />
                  <button onClick={() => removeItem(idx)} className="col-span-1 p-2 text-gray-400 hover:text-red-600" aria-label="מחק שורה">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <div className="text-left mt-2 text-sm font-bold text-gray-900 dark:text-white">
              סה"כ: {currency === 'ILS' ? '₪' : ''}{total.toLocaleString()} {currency !== 'ILS' ? currency : ''}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">הערות (יופיעו על המסמך)</label>
            <textarea className={inputCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {message && (
            <div className={`p-2 rounded-lg text-sm ${
              message.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
            }`}>
              {message.text}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
            ביטול
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !terminalId}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
          >
            <Download size={16} />
            {submitting ? 'מפיק…' : `הפק ${documentType === 'RE' ? 'קבלה' : 'דרישת תשלום'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
